import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  Briefcase, 
  FileText, 
  TrendingUp, 
  CheckCircle2, 
  Shield, 
  Lock, 
  Zap,
  Users,
  Star,
  Gift,
  Globe
} from 'lucide-react';
import { UpliftLogo, UpliftLogoWhite, UpliftShield } from '../components/UpliftLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UpliftHomeES = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscribers/count`);
        if (res.ok) {
          const data = await res.json();
          setSubscriberCount(data.count);
        }
      } catch (error) {
        // Non-critical: subscriber count fetch failed, use default
        console.debug('Subscriber count fetch failed:', error.message);
      }
    };
    fetchCount();
  }, []);

  const handleBetaSignup = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Por favor ingresa un correo válido');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        toast.success("¡Estás en la lista! Te contactaremos pronto.");
        setEmail('');
        setSubscriberCount(prev => prev + 1);
      } else {
        const data = await res.json();
        if (data.detail === 'Email already subscribed') {
          toast.info("¡Ya estás en la lista!");
        } else {
          toast.error('Algo salió mal');
        }
      }
    } catch (error) {
      toast.error('Error al registrarse');
    } finally {
      setIsSubmitting(false);
    }
  };

  const products = [
    {
      name: 'CareerBoost',
      tagline: 'Consigue Tu Trabajo Ideal',
      description: 'Creador de currículum con IA, rastreador de empleos y optimizador de contenido.',
      icon: <Briefcase className="w-8 h-8" />,
      color: 'from-indigo-500 to-indigo-700',
      link: '/es/careerboost',
      features: ['Currículum con IA', 'Rastreador de Empleos', 'Optimizador de LinkedIn'],
    },
    {
      name: 'BizPlanAI',
      tagline: 'Lanza Tu Negocio',
      description: 'Planes de negocio profesionales en minutos. Perfecto para préstamos e inversionistas.',
      icon: <FileText className="w-8 h-8" />,
      color: 'from-emerald-500 to-emerald-700',
      link: '/es/bizplanai',
      features: ['Planes de Negocio con IA', 'Proyecciones Financieras', 'Listo para Inversionistas'],
    },
    {
      name: 'CreditBoost',
      tagline: 'Repara Tu Crédito',
      description: 'Planes de acción personalizados y cartas de disputa para mejorar tu puntaje.',
      icon: <TrendingUp className="w-8 h-8" />,
      color: 'from-blue-500 to-blue-700',
      link: '/es/creditboost',
      features: ['Simulador de Puntaje', 'Planes de Acción', 'Cartas de Disputa'],
    },
  ];

  const trustBadges = [
    { icon: <Zap className="w-5 h-5" />, text: 'Potenciado por IA' },
    { icon: <Lock className="w-5 h-5" />, text: 'Pagos Seguros' },
    { icon: <Shield className="w-5 h-5" />, text: 'Datos Protegidos' },
    { icon: <CheckCircle2 className="w-5 h-5" />, text: 'Verificado por Stripe' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/es" data-testid="uplift-logo">
              <UpliftLogoWhite size="sm" showText={true} />
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/" className="flex items-center gap-1 text-slate-400 hover:text-white text-sm">
                <Globe className="w-4 h-4" />
                EN
              </Link>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white text-xs sm:text-sm px-2 sm:px-4">Iniciar Sesión</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm px-2 sm:px-4">
                  Comenzar
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <UpliftShield size={120} color="#6366F1" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Eleva Tu Vida
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 mb-4 max-w-3xl mx-auto">
            Tres herramientas poderosas. Una misión.
          </p>
          <p className="text-lg text-slate-500 mb-8 max-w-2xl mx-auto">
            Éxito profesional. Crecimiento empresarial. Libertad financiera. Te ayudamos a crecer en todo lo que importa.
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {trustBadges.map((badge, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700"
              >
                <span className="text-indigo-400">{badge.icon}</span>
                <span className="text-slate-300 text-sm">{badge.text}</span>
              </div>
            ))}
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-base sm:text-lg px-6 sm:px-8 w-full sm:w-auto">
                Comienza Tu Camino
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="#productos">
              <Button size="lg" variant="outline" className="text-base sm:text-lg px-6 sm:px-8 border-slate-700 text-slate-300 hover:bg-slate-800 w-full sm:w-auto">
                Explorar Productos
              </Button>
            </Link>
          </div>

          {subscriberCount > 0 && (
            <div className="mt-8 flex items-center justify-center gap-2 text-slate-400">
              <Users className="w-4 h-4" />
              <span>Únete a {subscriberCount}+ personas que ya se registraron</span>
            </div>
          )}
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-white">R</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Robert Elder III
                  </h3>
                  <p className="text-indigo-400">Fundador y CEO</p>
                </div>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="text-lg italic">
                  "Apliqué a más de 200 trabajos y casi no recibí respuestas. Fue entonces cuando me di cuenta de que el problema era mi currículum, no mis habilidades."
                </p>
                <p>
                  Construí CareerBoost para solucionar eso. Luego vi a emprendedores luchando por conseguir préstamos porque no podían pagar consultores de $2,000 para planes de negocio - así que creé BizPlanAI. Y cuando mis amigos me preguntaron cómo mejorar su crédito, construí CreditBoost.
                </p>
                <p>
                  <strong className="text-white">Uplift no es solo un negocio - es mi misión de hacer el éxito accesible para todos, no solo para quienes pueden pagar consultores caros.</strong>
                </p>
              </div>
              <div className="mt-6 flex items-center gap-4">
                <div className="flex">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <span className="text-slate-400 text-sm">Construyendo las herramientas que desearía haber tenido</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Products Grid */}
      <section id="productos" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Elige Tu Camino
            </h2>
            <p className="text-slate-400 text-lg">
              Cada herramienta está diseñada para elevar un área crítica de tu vida
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <Card 
                key={index}
                className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-all hover:-translate-y-2 overflow-hidden group"
              >
                <div className={`h-2 bg-gradient-to-r ${product.color}`} />
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${product.color} text-white flex items-center justify-center mb-5`}>
                    {product.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {product.name}
                  </h3>
                  <p className="text-indigo-400 font-medium mb-3 text-sm">{product.tagline}</p>
                  <p className="text-slate-400 mb-5 text-sm">{product.description}</p>
                  
                  <ul className="space-y-2 mb-6">
                    {product.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-300 text-xs">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {product.external ? (
                    <a href={product.link} target="_blank" rel="noopener noreferrer">
                      <Button className={`w-full bg-gradient-to-r ${product.color} hover:opacity-90 text-sm`}>
                        Probar {product.name}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  ) : (
                    <Link to={product.link}>
                      <Button className={`w-full bg-gradient-to-r ${product.color} hover:opacity-90 text-sm`}>
                        Explorar {product.name}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Beta Testers Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/20 rounded-full border border-indigo-500/30 mb-6">
            <Gift className="w-4 h-4 text-indigo-400" />
            <span className="text-indigo-300 text-sm font-medium">Oferta Por Tiempo Limitado</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Conviértete en Beta Tester
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Obtén <span className="text-indigo-400 font-bold">ACCESO GRATIS</span> a cambio de tu opinión honesta
          </p>
          <div className="bg-slate-900/80 rounded-2xl p-8 max-w-md mx-auto border border-slate-700">
            <div className="space-y-4 text-left mb-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-slate-300">Acceso gratis a todas las herramientas</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-slate-300">Ayuda a mejorar el producto</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-slate-300">Descuento de por vida al lanzar</span>
              </div>
            </div>
            <form onSubmit={handleBetaSignup} className="flex gap-2">
              <Input
                type="email"
                placeholder="Tu correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
              />
              <Button 
                type="submit" 
                className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Enviando...' : 'Unirme'}
              </Button>
            </form>
            <p className="text-slate-500 text-xs mt-4">
              Sin spam. Cancela cuando quieras.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <UpliftShield size={60} color="#6366F1" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ¿Por Qué Uplift?
          </h2>
          <p className="text-xl text-slate-400 mb-12">
            Fundado por Robert Elder III con una misión: <br />
            <span className="text-white font-medium">Hacer el éxito accesible para todos.</span>
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6">
              <div className="text-4xl font-bold text-indigo-400 mb-2">IA</div>
              <p className="text-slate-400">Potenciado</p>
              <p className="text-slate-500 text-sm mt-2">Inteligencia artificial que realmente ayuda</p>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-emerald-400 mb-2">Justo</div>
              <p className="text-slate-400">Precio</p>
              <p className="text-slate-500 text-sm mt-2">Pagos únicos, sin trucos</p>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-blue-400 mb-2">Real</div>
              <p className="text-slate-400">Resultados</p>
              <p className="text-slate-500 text-sm mt-2">Herramientas que realmente funcionan</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ¿Listo Para Crecer?
          </h2>
          <p className="text-xl text-slate-400 mb-8">
            Únete a {subscriberCount > 0 ? `${subscriberCount}+ personas` : 'otros'} que están transformando sus carreras, negocios y finanzas con Uplift.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-12 py-6">
              Comienza Tu Camino con Uplift
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <UpliftLogoWhite size="md" showText={true} className="mb-4" />
              <p className="text-slate-500 text-sm max-w-md">
                Uplift te ayuda a crecer en todo lo que importa. 
                Éxito profesional, crecimiento empresarial y libertad financiera - todo desde una fuente confiable.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Productos</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/es/careerboost" className="text-slate-500 hover:text-white">CareerBoost</Link></li>
                <li><Link to="/es/bizplanai" className="text-slate-500 hover:text-white">BizPlanAI</Link></li>
                <li><Link to="/es/creditboost" className="text-slate-500 hover:text-white">CreditBoost</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="text-slate-500 hover:text-white">Términos de Servicio</Link></li>
                <li><Link to="/privacy" className="text-slate-500 hover:text-white">Política de Privacidad</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-slate-500 text-sm">
                  Fundado por <span className="text-white font-medium">Robert Elder III</span>, CEO
                </p>
                <p className="text-slate-600 text-xs mt-1">
                  © 2024-2025 Uplift III / Robert Elder III. Todos los derechos reservados.
                </p>
              </div>
              <Link to="/" className="flex items-center gap-2 text-slate-500 hover:text-white text-sm">
                <Globe className="w-4 h-4" />
                English Version
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UpliftHomeES;
